import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { generateDTAI_ID, sendEncryptedMessage, listenToChat, getUserChatSessions } from '../services/chatService';
import { analyzeStats } from '../services/aiService';
import '../components/AIAesthetics.css';

function AIAnalyst({ stats }) {
  const { userProfile } = useAuth();
  const analysis = useMemo(() => analyzeStats(stats), [stats]);

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const prevMessagesLength = useRef(0);

  useEffect(() => {
    if (!sessionId) setSessionId(generateDTAI_ID());
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && userProfile?.uid) {
      let initialLoad = true;
      const unsubscribe = listenToChat(userProfile.uid, sessionId, (msgs) => {
        setMessages(msgs);
        
        if (!initialLoad && msgs.length > prevMessagesLength.current) {
          const lastMsg = msgs[msgs.length - 1];
          // If the message is from the admin, notify the user
          if (lastMsg && lastMsg.sender === 'ai') {
            setToastMessage("New response received from Admin.");
            setTimeout(() => setToastMessage(null), 4000);
            
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("DevTracker AI Insights", { body: "An Admin has responded to your inquiry." });
            }
          }
        }
        
        prevMessagesLength.current = msgs.length;
        initialLoad = false;
        
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
      return () => unsubscribe();
    }
  }, [sessionId, userProfile?.uid]);

  // Fetch Old Sessions 
  const fetchSessions = () => {
    if (userProfile?.uid) {
      getUserChatSessions(userProfile.uid).then(setSessions);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [userProfile?.uid, sessionId]); // refresh when new session is made

  const handleSend = async (e, forcedMessage = null) => {
    e?.preventDefault();
    const msg = (forcedMessage || inputText).trim();
    if (!msg || !userProfile?.uid || !sessionId) return;

    if (!forcedMessage) setInputText('');
    
    await sendEncryptedMessage(userProfile.uid, sessionId, msg, 'user');
    setIsTyping(true);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    setIsTyping(false);
  };

  const handleSuggestion = (text) => {
    handleSend(null, text);
  };

  return (
    <>
    {toastMessage && (
      <div className="chat-toast-notification">
        <i className="fa-solid fa-bell"></i> {toastMessage}
      </div>
    )}
    <div className="sarvam-dashboard-container animate-up" style={{display: 'flex', flexDirection: 'row'}}>
      
      {/* Left Sidebar: Chat History */}
      <div className="sarvam-left-sidebar">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h3 className="sarvam-sidebar-title" style={{margin: 0}}>Recent Chats</h3>
          <button onClick={fetchSessions} style={{background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--brand-accent)'}} title="Reload Sessions">
            <i className="fa-solid fa-rotate-right"></i>
          </button>
        </div>
        <div className="sarvam-session-list">
          {sessions.map(s => (
            <div 
              key={s.sessionId} 
              className={`sarvam-session-item ${sessionId === s.sessionId ? 'active' : ''}`}
              onClick={() => setSessionId(s.sessionId)}
            >
              <i className="fa-regular fa-message"></i> 
              <span>Session {s.sessionId.split('-')[1]}</span>
            </div>
          ))}
          {sessions.length === 0 && (
            <div style={{color: 'var(--sarvam-text-secondary)', fontSize: '0.85rem', padding: '10px 0'}}>No previous chats found.</div>
          )}
        </div>

        {/* Neural Profile Summary (v1.5) */}
        <div className="neural-profile-mini glass-card" style={{marginTop: 'auto', padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '16px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <span style={{fontSize: '0.65rem', color: 'var(--brand-accent)', fontWeight: 'bold', letterSpacing: '1px'}}>NEURAL_DNA // {analysis.vId}</span>
            <span style={{fontSize: '1rem', fontWeight: 'bold', color: '#fff'}}>{analysis.score}%</span>
          </div>
          
          <div className="classification-badge" style={{fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--brand-accent)', background: 'rgba(0, 255, 140, 0.1)', padding: '6px 10px', borderRadius: '50px', border: '1px solid rgba(0, 255, 140, 0.2)', textAlign: 'center', marginBottom: '15px'}}>
            <i className="fa-solid fa-microchip" style={{marginRight: '6px'}}></i> {analysis.classification.toUpperCase()}
          </div>

          <div className="mini-metrics" style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <div className="metric-item">
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#8b949e', marginBottom: '4px'}}>
                <span>Neural Density</span>
                <span style={{color: '#fff'}}>{analysis.metrics.neuralDensity}%</span>
              </div>
              <div style={{height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', overflow: 'hidden'}}>
                <div style={{height: '100%', width: `${analysis.metrics.neuralDensity}%`, background: 'var(--brand-accent)', boxShadow: '0 0 10px var(--brand-accent)'}}></div>
              </div>
            </div>

            <div className="metric-item">
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#8b949e', marginBottom: '4px'}}>
                <span>Arch Depth</span>
                <span style={{color: '#fff'}}>{analysis.metrics.archDepth}%</span>
              </div>
              <div style={{height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', overflow: 'hidden'}}>
                <div style={{height: '100%', width: `${analysis.metrics.archDepth}%`, background: '#8b5cf6', boxShadow: '0 0 10px #8b5cf6'}}></div>
              </div>
            </div>

            <div className="metric-item">
              <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#8b949e', marginBottom: '4px'}}>
                <span>Handshake Parity</span>
                <span style={{color: '#fff'}}>{analysis.metrics.parity}%</span>
              </div>
              <div style={{height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', overflow: 'hidden'}}>
                <div style={{height: '100%', width: `${analysis.metrics.parity}%`, background: '#0ea5e9', boxShadow: '0 0 10px #0ea5e9'}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Panel */}
      <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
        <div className="sarvam-header">
          <div className="sarvam-header-left">
            <h2>AI Insights</h2>
            <span className="sarvam-subtitle">Experiment with models, tune parameters, and test prompts</span>
          </div>
          <div className="sarvam-header-right">
            <div className="sarvam-badge-session">
              <i className="fa-solid fa-lock" style={{fontSize: '0.65rem'}}></i> E2EE • {sessionId}
            </div>
            <button className="sarvam-pill-btn secondary" onClick={() => setSessionId(generateDTAI_ID())}>
              <i className="fa-solid fa-plus"></i> New Chat
            </button>
          </div>
        </div>

        <div className="sarvam-chat-wrapper">
          <div className="sarvam-messages-area">
            {messages.length === 0 ? (
              <div className="sarvam-welcome-state">
                <h1 className="sarvam-serif-title">How can I help you today?</h1>
              </div>
            ) : (
              <div className="sarvam-chat-history">
                {messages.map((msg) => (
                  <div key={msg.id} className={`sarvam-message ${msg.sender === 'user' ? 'user' : 'ai'}`}>
                    {msg.sender === 'ai' && <div className="sarvam-avatar"><i className="fa-solid fa-sparkles"></i></div>}
                    {msg.sender === 'user' && <div className="sarvam-avatar user-avatar"><i className="fa-solid fa-user"></i></div>}
                    <div className="sarvam-bubble">{msg.text}</div>
                  </div>
                ))}
                {isTyping && (
                  <div className="sarvam-message ai">
                     <div className="sarvam-avatar"><i className="fa-solid fa-sparkles"></i></div>
                     <div className="sarvam-typing-indicator"><span></span><span></span><span></span></div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="sarvam-input-section">
            <form className="sarvam-input-box" onSubmit={(e) => handleSend(e)}>
              <input 
                type="text" 
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="What's on your mind?"
              />
              <button type="submit" disabled={!inputText.trim() || isTyping} className="sarvam-submit-btn">
                <i className="fa-solid fa-arrow-up"></i>
              </button>
            </form>

            {messages.length === 0 && (
              <div className="sarvam-action-chips">
                <button onClick={() => handleSuggestion("Summarize my GitHub statistics")} className="sarvam-chip">
                  <i className="fa-solid fa-align-left"></i> Summarize stats
                </button>
                <button onClick={() => handleSuggestion("Predict my market value based on my DNA")} className="sarvam-chip">
                  <i className="fa-solid fa-chart-line"></i> Predict value
                </button>
                <button onClick={() => handleSuggestion("Draft an email to a recruiter with my profile link")} className="sarvam-chip">
                  <i className="fa-solid fa-pen"></i> Draft an email
                </button>
                <button onClick={() => handleSuggestion("Explain my logic composition score")} className="sarvam-chip">
                  <i className="fa-solid fa-table-cells-large"></i> Explain concepts
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default AIAnalyst;
